import { useState, useEffect, useRef } from 'react';

// Cinematic reveal timing constants
const HERO_DELAY_MS = 600; // Hero appears after 600ms
const FORM_EXTRA_DELAY_MS = 500; // Form appears 500ms after hero
const SCROLL_THRESHOLD_PX = 50; // 50px scroll triggers immediate reveal

export function useSignupReveal() {
  const [showHero, setShowHero] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [reducedMotion, setReducedMotion] = useState(false);
  const hasTriggeredHeroRef = useRef(false);
  const hasTriggeredFormRef = useRef(false);

  useEffect(() => {
    // Check for reduced motion preference
    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    const prefersReducedMotion = mediaQuery.matches;
    setReducedMotion(prefersReducedMotion);

    // If reduced motion is preferred, show everything immediately
    if (prefersReducedMotion) {
      setShowHero(true);
      setShowForm(true);
      hasTriggeredHeroRef.current = true;
      hasTriggeredFormRef.current = true;
      return;
    }

    // Trigger hero reveal (only runs once)
    const triggerHeroReveal = () => {
      if (hasTriggeredHeroRef.current) return;
      hasTriggeredHeroRef.current = true;
      setShowHero(true);

      // Schedule form reveal after hero
      setTimeout(() => {
        if (hasTriggeredFormRef.current) return;
        hasTriggeredFormRef.current = true;
        setShowForm(true);
      }, FORM_EXTRA_DELAY_MS);
    };

    // Trigger immediate full reveal (for scroll)
    const triggerFullReveal = () => {
      if (!hasTriggeredHeroRef.current) {
        hasTriggeredHeroRef.current = true;
        setShowHero(true);
      }
      if (!hasTriggeredFormRef.current) {
        hasTriggeredFormRef.current = true;
        setShowForm(true);
      }
    };

    // Set up hero timeout trigger
    const heroTimeoutId = setTimeout(triggerHeroReveal, HERO_DELAY_MS);

    // Set up scroll trigger (reveals everything immediately)
    const handleScroll = () => {
      const scrollY = window.scrollY;
      if (scrollY >= SCROLL_THRESHOLD_PX) {
        triggerFullReveal();
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });

    // Cleanup
    return () => {
      clearTimeout(heroTimeoutId);
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  return { showHero, showForm, reducedMotion };
}
